// The Commons garden and the shared :00/:30 focus sessions.
//
// The garden grows from everyone's focus minutes each week. A finished focus block adds its
// minutes to a small pending number here; the next presence beacon carries it (app/presence.js)
// and the server adds it to one weekly total, capped per browser per day. The reply says how many
// counted; the rest go back in the queue for a later beacon, until they are PENDING_MAX_AGE old.
// The pending number sits under a key outside "revault:", so a restored backup never sends the
// same minutes twice. No names, no ids beyond the presence beacon's random one, nothing about
// what was studied.
const PENDING_KEY = "rv-commons-pending";
// Same cap as the server's (src/server/lib/commons.js), so a browser never queues more.
export const DEVICE_DAY_CAP = 240;
// Minutes nobody counted a day after the last finished block are let go.
export const PENDING_MAX_AGE = 24 * 3600000;

const settingOn = () => {
	try { const s = JSON.parse(localStorage.getItem("revault:settings") || "null"); return !(s && s.commonsGarden === false); } catch { return true; }
};
const p2 = (n) => (n < 10 ? "0" : "") + n;
const dayOf = (d) => d.getFullYear() + "-" + p2(d.getMonth() + 1) + "-" + p2(d.getDate());
const ms = (now) => (now instanceof Date ? now.getTime() : Number(now) || Date.now());

function read(now) {
	try {
		const v = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
		if (!(v && Number.isInteger(v.m) && v.m > 0 && typeof v.d === "string")) return null;
		if (Number.isFinite(v.t) && ms(now) - v.t > PENDING_MAX_AGE) { write(null); return null; }
		return v;
	} catch { return null; }
}
function write(v) { try { if (v && v.m > 0) localStorage.setItem(PENDING_KEY, JSON.stringify(v)); else localStorage.removeItem(PENDING_KEY); } catch { /* storage off: the minutes just stay here */ } }

// A finished focus block. Today's queue never goes past the daily cap.
export function addCommons(minutes, now = new Date()) {
	const n = Math.floor(Number(minutes));
	if (!(n > 0) || !settingOn()) return;
	const d = dayOf(now);
	const cur = read(now);
	const base = cur && cur.d === d ? cur.m : 0;
	// Yesterday's leftovers (a tab that never beaconed again) go along with today's, still capped.
	const carry = cur && cur.d !== d ? Math.min(cur.m, DEVICE_DAY_CAP) : 0;
	write({ d, m: Math.min(DEVICE_DAY_CAP, base + carry + n), t: ms(now) });
}
// When the minutes last taken were queued, so ones put back keep their age.
let takenAt = 0;
// What the next beacon carries. Taken out first, so two windows don't both send it.
export function takeCommons(now = new Date()) {
	if (!settingOn()) { write(null); return 0; }
	const cur = read(now);
	if (!cur) return 0;
	write(null);
	takenAt = Number.isFinite(cur.t) ? cur.t : ms(now);
	return Math.min(cur.m, DEVICE_DAY_CAP);
}
// Minutes that didn't count (the beacon failed, or the server only took some): put them back.
export function returnCommons(n, now = new Date()) {
	if (!(n > 0) || !settingOn()) return;
	const cur = read(now);
	const t = Math.max(cur && Number.isFinite(cur.t) ? cur.t : 0, takenAt || ms(now));
	if (ms(now) - t > PENDING_MAX_AGE) return;
	write({ d: cur ? cur.d : dayOf(new Date(ms(now))), m: Math.min(DEVICE_DAY_CAP, (cur ? cur.m : 0) + n), t });
}

// --- the garden's words --------------------------------------------------------------------------
// Stages come from the server (it knows the real total); hours come rounded to 5, or not at all
// below 10.
export const STAGES = ["Seeds", "Sprouts", "Seedlings", "Young plants", "Flowers", "A full garden", "A grove"];
export function gardenText(g) {
	if (!g || !Number.isInteger(g.s) || g.s < 1) return ""; // nothing to show until it sprouts
	const stage = STAGES[Math.min(g.s, STAGES.length - 1)];
	const hours = Number.isInteger(g.h) && g.h >= 10 ? " · about " + g.h.toLocaleString("en-US") + " hours of focus this week" : "";
	return "Commons garden: " + stage.toLowerCase() + hours;
}
export function paintGarden(el, g, enabled) {
	if (!el) return false;
	const text = enabled === false ? "" : gardenText(g);
	if (el.textContent !== text) el.textContent = text;
	el.hidden = !text;
	return !!text;
}

// --- shared focus sessions -----------------------------------------------------------------------
// Sessions start together at :00 and :30 by the local clock: 25 minutes of focus, 5 of break.
// Up to JOIN_LATE minutes past the start you join the running one (it ends with everyone);
// later than that you wait for the next.
export const SYNC_WORK = 25;
export const JOIN_LATE = 5;
export function syncPlan(nowMs) {
	const d = new Date(nowMs);
	d.setMinutes(d.getMinutes() < 30 ? 0 : 30, 0, 0);
	const slot = d.getTime();
	if (nowMs - slot <= JOIN_LATE * 60000) return { start: slot, end: slot + SYNC_WORK * 60000, wait: 0 };
	const next = new Date(slot + 30 * 60000);
	return { start: next.getTime(), end: next.getTime() + SYNC_WORK * 60000, wait: next.getTime() - nowMs };
}
